import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, X, ArrowUpRight, BookOpen, HeartHandshake, Sprout, Globe, Quote } from 'lucide-react';
import { InlineText } from './InlineEditable';
import { YoutubeVideoEditButton } from './YoutubeVideoEdit';
import { usePublicSettings } from '../hooks/usePublicSettings';

// Real text extracted from https://yekuv.org/about/
const FOUNDING_TEXT = `21. Yüzyıl Eğitim ve Kültür Vakfı (YEKÜV), ülkemizde yaşanan sorunların temelinde eğitim olduğunu görerek, bu sorunların çözümüne katkı sağlamak amacıyla, 1992 yılında on Cerrahpaşa Tıp Fakültesi öğrencisine karşılıksız eğitim bursu vermek üzere kuruldu.`;

const MISSION_TEXT = `Özellikle okumak için kırsaldan İstanbul'a gelen, başarılı ancak maddi olanakları kısıtlı üniversite gençlerine eğitimde fırsat eşitliği sunmak ve en iyi düzeyde yetişmelerine katkı sağlamak amacıyla çalışan YEKÜV, her yıl bünyesine kattığı yeni bursiyerleriyle büyüyen bir vakıftır.`;

const COMMUNITY_TEXT = `Bursiyerleriyle maddi katkıyı aşan bir anlayışla iletişim kuran YEKÜV olarak, gençlerin eğitimlerini sürdürürken YEKÜV'lü diğer arkadaşları ile birlikte olabilecekleri, soru ve sorunlarını paylaşabilecekleri ortamlar hazırlıyor, uzaktaki ailelerinin sıcaklığını yaşamalarını sağlamak için elimizden geleni yapıyoruz.`;

const VISION_TEXT = `Çağın gerektirdiği yeniliklere açık, hür ve bilimsel düşünceye sahip, eğitimli, kültürlü, hukuka saygılı, düşünen, üreten, tartışan, yeteneklerini geliştirebilen bireylerden oluşan bir toplum vizyonu ile sosyal ve kültürel projeler üretirken; bugün farklı alanlarda eğitim gören öğrencilerimizle ilk günkü heyecan ve istekle çalışmalarımıza devam ediyoruz.`;

const INTRO_VIDEO_ID = 'SeXWvU5VWO4'; // Default Yeküv Tanıtım Filmi (overridable via `home_intro_video_id` setting)

const VALUES = [
  { icon: BookOpen, title: 'Karşılıksız Eğitim', text: 'Her çocuğun eğitime erişim hakkını karşılıksız desteklerle güvenceye alırız.' },
  { icon: HeartHandshake, title: 'Bireyle Temas', text: 'Sadece burs değil; rehberlik, mentorluk ve aidiyet sunarız.' },
  { icon: Sprout, title: 'Kültürel Gelişim', text: 'Sanat, bilim ve kültür atölyeleriyle bireyin bütünsel gelişimine katkı veririz.' },
  { icon: Globe, title: 'Ulusal Etki', text: 'Türkiye\'nin dört bir yanında sahada; fırsat eşitliği için fark yaratıyoruz.' },
];

export default function OurStorySection() {
  const [showVideo, setShowVideo] = useState(false);
  const settings = usePublicSettings();
  const videoId = (settings.home_intro_video_id || INTRO_VIDEO_ID).trim();
  const coverUrl = (settings.home_intro_video_id_cover || '').trim();

  return (
    <>
      {/* Editorial Story — replaces generic mission section with real /about/ content */}
      <section className="relative bg-cream overflow-hidden" data-testid="our-story-section">
        {/* Massive typographic 1992 anchor */}
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          <div className="absolute -top-10 right-[-80px] md:right-[-40px] font-display text-[200px] md:text-[340px] lg:text-[420px] leading-none text-ink-600 font-bold tracking-tighter opacity-[0.04]">
            1992
          </div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          {/* Section header */}
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold">
                <span className="w-8 h-px bg-coral-300" />
                <InlineText settingKey="home_story_kicker" fallback="Hikâyemiz" as="span" />
              </div>
              <InlineText
                settingKey="home_story_title"
                fallback={'<span class="italic text-coral-300">10 öğrenciyle</span><br />başlayan<br />bir misyon.'}
                as="h2"
                html
                multiline
                className="font-display text-4xl md:text-5xl lg:text-[72px] mt-5 leading-[0.98] text-ink-600"
              />
            </div>
            <div className="lg:col-span-5 lg:pt-10 flex items-end">
              <InlineText
                settingKey="home_story_founding_text"
                fallback={FOUNDING_TEXT}
                as="p"
                multiline
                className="text-ink-500 text-base md:text-lg leading-relaxed"
              />
            </div>
          </div>

          {/* Editorial layout: Video card + Story text + Stats */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Video / Visual showcase */}
            <div className="lg:col-span-7 relative">
              {/* Admin edit button — floats over card in edit mode */}
              <div className="absolute top-4 right-4 z-20">
                <YoutubeVideoEditButton
                  settingKey="home_intro_video_id"
                  currentValue={videoId}
                  label="Videoyu Değiştir"
                />
              </div>
              <button
                onClick={() => setShowVideo(true)}
                data-testid="intro-video-play"
                className="group relative w-full aspect-[16/10] rounded-[28px] overflow-hidden shadow-2xl shadow-ink-900/20 bg-ink-600 block"
                aria-label="YEKÜV Tanıtım Filmini İzle"
              >
                <img
                  src={coverUrl || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                  alt="YEKÜV Tanıtım Filmi"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  onError={(e) => { if (!coverUrl) e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/15 to-transparent" />

                {/* Play button */}
                <div className="absolute inset-0 grid place-items-center">
                  <div className="relative">
                    <span className="absolute inset-0 rounded-full bg-gold/30 animate-ping" />
                    <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-gold text-ink-700 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 md:w-10 md:h-10 ml-1" fill="currentColor" />
                    </div>
                  </div>
                </div>

                {/* Top-left badge */}
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-cream/95 backdrop-blur text-ink-700 rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em]">
                  <span className="w-2 h-2 rounded-full bg-coral-300 animate-pulse" />
                  <InlineText settingKey="home_intro_video_badge" fallback="YEKÜV Tanıtım Filmi" as="span" />
                </div>

                {/* Bottom caption */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-cream">
                  <InlineText settingKey="home_intro_video_kicker" fallback="Bizden Bir Bakış" as="div"
                    className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold" />
                  <InlineText settingKey="home_intro_video_caption" fallback="YEKÜV'ün hikâyesini izleyin" as="div"
                    className="font-display text-2xl md:text-3xl mt-1 leading-tight" />
                </div>
              </button>

              {/* Inline stats below video */}
              <div className="mt-6 grid grid-cols-3 gap-0 bg-white border border-ink-100 rounded-[20px] overflow-hidden" data-testid="story-stats">
                {[
                  { n: '10', l: 'İlk bursiyer', sub: 'Cerrahpaşa Tıp 1992' },
                  { n: '33+', l: 'Yıllık miras', sub: 'Kesintisiz hizmet' },
                  { n: '12.500+', l: 'Bursiyer', sub: 'Hayata dokunduğumuz' },
                ].map((s, i) => (
                  <div key={i} className={`p-5 md:p-6 ${i < 2 ? 'border-r border-ink-100' : ''}`} data-testid={`story-stat-${i + 1}`}>
                    <InlineText settingKey={`home_story_stat_${i + 1}_number`} fallback={s.n} as="div"
                      className="font-display text-3xl md:text-4xl text-ink-600 tabular leading-none" />
                    <InlineText settingKey={`home_story_stat_${i + 1}_label`} fallback={s.l} as="div"
                      className="mt-3 text-[11px] uppercase tracking-[0.25em] text-coral-300 font-bold" />
                    <InlineText settingKey={`home_story_stat_${i + 1}_sub`} fallback={s.sub} as="div"
                      className="mt-1 text-xs text-ink-400" />
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — Story narrative */}
            <div className="lg:col-span-5 flex flex-col">
              {/* Mission text */}
              <div className="bg-white rounded-[24px] p-7 md:p-8 border border-ink-100">
                <InlineText settingKey="home_story_mission_kicker" fallback="Misyonumuz" as="div" className="text-[10px] uppercase tracking-[0.3em] text-coral-300 font-bold" />
                <InlineText settingKey="home_story_mission_text" fallback={MISSION_TEXT} as="p" multiline className="mt-4 text-ink-500 text-base leading-relaxed" />
              </div>

              {/* Pull quote — pulled from real text */}
              <div className="mt-6 relative bg-ink-600 text-cream rounded-[24px] p-7 md:p-9 overflow-hidden">
                <div className="absolute -top-12 -right-12 w-[200px] h-[200px] rounded-full bg-gold/15 blur-3xl pointer-events-none" />
                <Quote className="w-9 h-9 text-gold relative" />
                <InlineText
                  settingKey="home_story_quote_text"
                  fallback={'"Maddi katkıyı aşan bir anlayışla iletişim kuruyor; uzaktaki ailelerinin sıcaklığını yaşamalarını sağlıyoruz."'}
                  as="p" multiline
                  className="mt-3 font-display text-xl md:text-2xl italic leading-snug relative"
                />
                <div className="mt-5 flex items-center gap-3 text-cream/70 text-xs relative">
                  <span className="w-6 h-px bg-gold" />
                  <InlineText settingKey="home_story_quote_author" fallback="YEKÜV Felsefesi" as="span" />
                </div>
              </div>

              {/* CTA */}
              <Link
                to="/kurumsal"
                className="mt-6 group inline-flex items-center justify-between bg-cream-200 hover:bg-ink-600 hover:text-cream rounded-[20px] p-6 transition-colors"
                data-testid="story-tarihce-cta"
              >
                <div>
                  <InlineText settingKey="home_story_cta_kicker" fallback="Devamı" as="div"
                    className="text-[10px] uppercase tracking-[0.3em] text-coral-300 group-hover:text-gold font-bold" />
                  <InlineText settingKey="home_story_cta_title" fallback="Tüm tarihçemizi keşfedin" as="div"
                    className="font-display text-2xl mt-1.5 text-ink-600 group-hover:text-cream" />
                </div>
                <ArrowUpRight className="w-6 h-6 text-ink-600 group-hover:text-gold transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </div>

          {/* Vision Statement Strip */}
          <div className="mt-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <InlineText settingKey="home_story_vision_kicker" fallback="Vizyonumuz" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
              <InlineText settingKey="home_story_vision_title"
                fallback={'Düşünen, üreten, <span class="italic text-coral-300">üretken</span> bireyler.'}
                as="h3" html multiline
                className="font-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05] text-ink-600" />
            </div>
            <div className="lg:col-span-8">
              <InlineText settingKey="home_story_vision_text" fallback={VISION_TEXT} as="p" multiline
                className="text-ink-500 text-base md:text-lg leading-relaxed" />
            </div>
          </div>

          {/* Story to community paragraph */}
          <div className="mt-16 max-w-3xl mx-auto text-center">
            <InlineText settingKey="home_story_community_kicker" fallback="Topluluk" as="div"
              className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
            <InlineText settingKey="home_story_community_text" fallback={COMMUNITY_TEXT} as="p" multiline
              className="mt-4 font-display text-xl md:text-2xl lg:text-[28px] leading-snug text-ink-600 italic" />
          </div>

          {/* Values grid (kept from old MissionSection) */}
          <div className="mt-20">
            <InlineText settingKey="home_story_values_kicker" fallback="Değerlerimiz" as="div"
              className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold text-center" />
            <InlineText settingKey="home_story_values_title"
              fallback={'33 yıldır <span class="italic text-coral-300">değişmeyen</span> ilkelerimiz.'}
              as="h3" html multiline
              className="font-display text-3xl md:text-4xl mt-3 leading-tight text-ink-600 text-center max-w-2xl mx-auto" />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {VALUES.map((v, i) => {
                const Icon = v.icon;
                const n = i + 1;
                return (
                  <div key={i} className="group relative bg-white rounded-3xl p-7 border border-ink-100 hover:border-gold transition-all hover:-translate-y-1 hover:shadow-xl" data-testid={`value-${i}`}>
                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gold/0 group-hover:bg-gold/10 transition-colors" />
                    <div className="relative">
                      <div className="w-12 h-12 rounded-2xl bg-ink-50 grid place-items-center text-ink-600 group-hover:bg-ink-600 group-hover:text-gold transition-colors">
                        <Icon className="w-5 h-5" />
                      </div>
                      <InlineText settingKey={`home_story_value_${n}_title`} fallback={v.title} as="div"
                        className="mt-5 font-display text-2xl text-ink-600 leading-snug" />
                      <InlineText settingKey={`home_story_value_${n}_text`} fallback={v.text} as="p" multiline
                        className="mt-2 text-ink-500 text-sm leading-relaxed" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Video lightbox */}
      {showVideo && (
        <div
          className="fixed inset-0 z-[100] bg-ink-900/95 backdrop-blur-sm grid place-items-center p-4 md:p-8"
          onClick={() => setShowVideo(false)}
          data-testid="intro-video-lightbox"
        >
          <button
            onClick={() => setShowVideo(false)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors"
            aria-label="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title="YEKÜV Tanıtım Filmi"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </>
  );
}
